import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  H2,
  H3,
  HTMLSelect,
  Tag,
  Callout,
  Dialog,
  DialogBody,
  DialogFooter,
  InputGroup,
  Intent,
  Icon
} from '@blueprintjs/core';
import { useNavigate } from 'react-router-dom';
import {
  SAT_SEMESTERS,
  createDefaultSatHistory,
  createDefaultSemesterData,
  subscribeToSatHistory,
  updateSatPodium
} from '../firebase/satHistoryService';

function SatHistory() {
  const navigate = useNavigate();
  const [selectedSemester, setSelectedSemester] = useState(SAT_SEMESTERS[SAT_SEMESTERS.length - 1]);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState(null);
  const [satData, setSatData] = useState(() => createDefaultSatHistory());
  const [pendingPodiumEdits, setPendingPodiumEdits] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('admin') === 'true') {
      setIsAdminModalOpen(true);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToSatHistory((data) => {
      setSatData(data);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (!isAdminMode) {
      setSaveError(null);
    }
  }, [isAdminMode]);

  useEffect(() => {
    setSaveError(null);
  }, [selectedSemester]);

  const currentData = useMemo(
    () => satData[selectedSemester] || createDefaultSemesterData(),
    [satData, selectedSemester]
  );
  const hasPendingEdits = useMemo(
    () => Boolean(pendingPodiumEdits[selectedSemester]),
    [pendingPodiumEdits, selectedSemester]
  );

  const openAdminModal = () => {
    setAdminError(null);
    setIsAdminModalOpen(true);
  };

  const closeAdminModal = () => {
    setIsAdminModalOpen(false);
    setAdminPassword('');
    setAdminError(null);
  };

  const handleAdminPasswordSubmit = () => {
    if (adminPassword === process.env.REACT_APP_ADMIN_PASSWORD) {
      setSaveError(null);
      setIsAdminMode(true);
      closeAdminModal();
    } else {
      setAdminError('Incorrect password. Please try again.');
      setAdminPassword('');
    }
  };

  const handlePodiumChange = (podiumKey, field, value) => {
    setSaveError(null);
    setPendingPodiumEdits((prev) => {
      const existing = prev[selectedSemester] || {};
      const basePodium =
        existing[podiumKey] ||
        currentData[podiumKey] ||
        createDefaultSemesterData()[podiumKey];
      const updatedPodium = {
        ...basePodium,
        label: podiumKey === 'champions' ? 'Champions' : 'Runner-Up',
        [field]: value
      };

      return {
        ...prev,
        [selectedSemester]: {
          ...existing,
          [podiumKey]: updatedPodium
        }
      };
    });
  };

  const handleConfirmChanges = async () => {
    const pending = pendingPodiumEdits[selectedSemester];
    if (!pending) {
      setIsAdminMode(false);
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const updatedEntry = await updateSatPodium(selectedSemester, pending);

      setSatData((prev) => ({
        ...prev,
        [selectedSemester]: updatedEntry
      }));

      setPendingPodiumEdits((prev) => {
        const { [selectedSemester]: _, ...rest } = prev;
        return rest;
      });

      setIsAdminMode(false);
    } catch (error) {
      console.error('Error confirming SAT podium changes:', error);
      setSaveError(error.message || 'Failed to save SAT changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSat = () => {
    // Placeholder action for future implementation
    console.log('Add SAT action triggered for future implementation.');
  };

  const renderPodiumBadge = (podiumKey, iconName) => {
    if (podiumKey === 'runnersUp') {
      return (
        <div
          style={{
            width: '46px',
            height: '46px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f9fbff 0%, #cdd5e6 100%)',
            border: '1px solid rgba(120, 134, 159, 0.55)',
            boxShadow:
              '0 6px 14px rgba(15, 23, 42, 0.22), inset 0 2px 4px rgba(255, 255, 255, 0.75), inset 0 -3px 6px rgba(148, 163, 184, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: '78%',
              height: '78%',
              borderRadius: '50%',
              background: 'radial-gradient(circle at 30% 30%, #fdfdff 0%, #e0e6f2 60%, #c0c8da 100%)',
              border: '1px solid rgba(148, 163, 184, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#1f2937',
              letterSpacing: '0.02em'
            }}
          >
            2
          </div>
        </div>
      );
    }

    return <Icon icon={iconName} size={26} style={{ color: 'var(--accent-primary)' }} />;
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <Button
        minimal
        icon="arrow-left"
        onClick={() => navigate('/other')}
        style={{ marginBottom: '1rem' }}
      >
        Back to Other Tools
      </Button>

      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <H2 style={{ marginBottom: '0.5rem' }}>SAT History</H2>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto' }}>
          The SAT is a semester-long tournament built from every ACT. Explore the championship scorecards,
          celebrate the winners, and trace the stories that defined each semester since Fall 2023.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <span style={{ fontWeight: 600, marginRight: '0.75rem' }}>Semester:</span>
          <HTMLSelect
            options={SAT_SEMESTERS}
            value={selectedSemester}
            onChange={(e) => setSelectedSemester(e.target.value)}
          />
        </div>
        {!isAdminMode && (
          <Button icon="lock" onClick={openAdminModal}>
            Admin Mode
          </Button>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}
      >
        {[
          { key: 'champions', icon: 'trophy' },
          { key: 'runnersUp', icon: 'medal' }
        ].map(({ key, icon }) => {
          const isRunnerUp = key === 'runnersUp';
          const podium =
            (pendingPodiumEdits[selectedSemester] && pendingPodiumEdits[selectedSemester][key]) ||
            currentData[key];
          return (
          <Card
            key={podium.label}
            style={{
              padding: '1.75rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              borderRadius: 'var(--radius)',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {renderPodiumBadge(key, icon)}
              <div>
                <Tag
                  minimal
                  intent={podium.label === 'Champions' ? Intent.PRIMARY : Intent.NONE}
                  style={{
                    marginBottom: '0.25rem',
                    background: isRunnerUp ? 'rgba(148, 163, 184, 0.15)' : undefined,
                    color: isRunnerUp ? 'var(--text-secondary)' : undefined,
                    borderRadius: '999px',
                    padding: '0 0.5rem'
                  }}
                >
                  {podium.label}
                </Tag>
                {isAdminMode ? (
                  <InputGroup
                    value={podium.name}
                    onChange={(e) => handlePodiumChange(key, 'name', e.target.value)}
                    placeholder="Enter team name"
                    style={{ fontWeight: 700 }}
                  />
                ) : (
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{podium.name}</div>
                )}
              </div>
            </div>
          </Card>
        );
        })}
      </div>

      <Card
        style={{
          padding: '2rem',
          marginBottom: '2rem',
          borderRadius: 'var(--radius)',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <H3 style={{ margin: 0 }}>Final Scorecard</H3>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '0.5rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.75rem 1rem',
            border: '1px solid var(--border)',
            marginBottom: '1rem'
          }}
        >
          <div>Team Players</div>
          <div style={{ textAlign: 'right' }}>Score</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {currentData.finalScorecard.map((row, index) => (
            <div
              key={`${row.teamPlayers}-${index}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '0.75rem',
                alignItems: 'center',
                padding: '0.85rem 1rem',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)'
              }}
            >
              <div style={{ fontWeight: 600 }}>{row.teamPlayers}</div>
              <div style={{ fontWeight: 700, textAlign: 'right' }}>{row.score}</div>
            </div>
          ))}
        </div>
      </Card>

      {isAdminMode && (
        <Card
          style={{
            padding: '1.75rem',
            borderRadius: 'var(--radius)',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow)'
          }}
        >
          <H3 style={{ marginTop: 0 }}>Admin Tools</H3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Once SAT results conclude, use this control to append the new semester record, upload final scorecards,
            and broadcast winners to the history timeline.
          </p>
          {saveError && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: '1rem' }}>
              {saveError}
            </Callout>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button icon="add" intent={Intent.PRIMARY} onClick={handleAddSat}>
              Add SAT Result
            </Button>
            <Button
              intent={Intent.SUCCESS}
              icon="confirm"
              onClick={handleConfirmChanges}
              disabled={!hasPendingEdits || isSaving}
              loading={isSaving}
            >
              Confirm Podium Changes
            </Button>
          </div>
        </Card>
      )}

      <Dialog
        isOpen={isAdminModalOpen}
        onClose={closeAdminModal}
        title="Admin Access Required"
        icon="lock"
        style={{ width: '400px' }}
        canEscapeKeyClose
        canOutsideClickClose
      >
        <DialogBody>
          <p style={{ marginBottom: '1rem' }}>
            Enter the admin password to unlock SAT management features.
          </p>
          {adminError && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: '1rem' }}>
              {adminError}
            </Callout>
          )}
          <InputGroup
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            placeholder="Enter admin password..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAdminPasswordSubmit();
              }
            }}
            autoFocus
          />
        </DialogBody>
        <DialogFooter>
          <Button onClick={closeAdminModal}>Cancel</Button>
          <Button
            intent={Intent.PRIMARY}
            onClick={handleAdminPasswordSubmit}
            disabled={!adminPassword.trim()}
          >
            Access Admin Mode
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default SatHistory;
